import { useCallback, useMemo } from 'react';
import { useIssueSearch, useAllIssues } from '@/hooks/useIssues.js';
import { getOpenBlockers } from '@/lib/issue-links.js';
import { useAllStatuses } from '@/hooks/useMeta.js';
import { IssueList } from '@/features/issues/IssueList.js';
import { IssueFilters, buildJql, extractFilterOptions } from '@/components/IssueFilters.js';
import { Pagination } from '@/components/Pagination.js';
import { useUrlFilterState, useUrlTextFilter, useUrlPage } from '@/hooks/useUrlFilterState.js';
import { PageHeader } from '@/components/PageHeader.js';
import { jqlString, looksLikeJql } from '@/lib/jql.js';
import { LoadingState, ErrorState, EmptyState } from '@/components/PageState.js';
import type { JiraIssueSummary } from '@shared/types.js';

// Jira can ask for issues that have a blocker, not whether the blocker is finished
const HAS_BLOCKER_JQL = 'issueLinkType = "is blocked by"';
const BLOCKED_JQL = `${HAS_BLOCKER_JQL} AND statusCategory != Done ORDER BY updated DESC`;
const PAGE_SIZE = 50;

const QUICK_FILTERS = [
  { label: 'Recently updated', jql: 'updated >= -7d ORDER BY updated DESC' },
  { label: 'Recently created', jql: 'created >= -7d ORDER BY created DESC' },
  { label: 'In progress', jql: 'status = "In Progress" ORDER BY updated DESC' },
  { label: 'Highest priority', jql: 'priority = Highest ORDER BY updated DESC' },
  { label: 'Blocked', jql: BLOCKED_JQL },
];

export function SearchPage() {
  const [query, setQuery] = useUrlTextFilter('q');
  const [submittedQuery, setSubmittedQuery] = useUrlTextFilter('jql');
  const { filters, setFilters } = useUrlFilterState('updated', 'DESC', true); // showDone=true by default for search
  const [page, setPage] = useUrlPage();

  // Get all statuses from API (not from loaded results)
  const { data: apiStatuses } = useAllStatuses();
  const statuses = useMemo(() => apiStatuses || [], [apiStatuses]);

  const jql = useMemo(() => {
    if (!submittedQuery) return '';

    // If raw JQL, use as-is (user controls everything)
    if (looksLikeJql(submittedQuery)) {
      return submittedQuery;
    }

    // Text search with filters
    const text = `text ~ ${jqlString(submittedQuery)}`;
    return buildJql(filters.blockedOnly ? `${text} AND ${HAS_BLOCKER_JQL}` : text, filters);
  }, [submittedQuery, filters]);

  // "Blocked" means a blocker that is not done. Jira cannot filter on that, so
  // these searches load every match and the finished blockers are dropped here.
  const blockedOnly = submittedQuery === BLOCKED_JQL || (!looksLikeJql(submittedQuery) && filters.blockedOnly);
  const paged = useIssueSearch(blockedOnly ? '' : jql, page, PAGE_SIZE);
  const everything = useAllIssues(blockedOnly ? jql : '');

  const stillBlocked = useMemo(
    () => (everything.data ?? []).filter((issue) => getOpenBlockers(issue.links).length > 0),
    [everything.data],
  );
  const data = useMemo(() => {
    if (!blockedOnly) return paged.data;
    if (!everything.data) return undefined;
    const lastPage = Math.max(1, Math.ceil(stillBlocked.length / PAGE_SIZE));
    const start = (Math.min(page, lastPage) - 1) * PAGE_SIZE;
    return { issues: stillBlocked.slice(start, start + PAGE_SIZE), total: stillBlocked.length, isLast: true };
  }, [blockedOnly, paged.data, everything.data, stillBlocked, page]);
  const isLoading = blockedOnly ? everything.isLoading : paged.isLoading;
  const error = blockedOnly ? everything.error : paged.error;

  const allIssues = useMemo(() => (data?.issues ?? []) as JiraIssueSummary[], [data?.issues]);
  // Assignees: extract from loaded results (no global endpoint for all assignees)
  const { assignees } = useMemo(() => extractFilterOptions(allIssues), [allIssues]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(query);
    setPage(1);
  }, [query, setSubmittedQuery, setPage]);

  const handleQuickFilter = useCallback((jqlStr: string) => {
    setQuery(jqlStr);
    setSubmittedQuery(jqlStr);
    setPage(1);
  }, [setQuery, setSubmittedQuery, setPage]);

  const handleSort = useCallback((field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortField: field,
      sortDir: prev.sortField === field && prev.sortDir === 'DESC' ? 'ASC' : 'DESC',
    }));
    setPage(1);
  }, [setFilters, setPage]);

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1);
  }, [setFilters, setPage]);

  const isJql = looksLikeJql(submittedQuery);

  return (
    <div>
      <PageHeader title="Search" meta={data && !isLoading ? `${data.total}${data.isLast ? '' : '+'} results` : undefined} />

      <form onSubmit={handleSubmit} className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search text, or enter JQL (e.g. project = LH AND status = 'In Progress')"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            aria-label="Search issues"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          Search
        </button>
      </form>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500">Quick filters</span>
        {QUICK_FILTERS.map((qf) => {
          const active = submittedQuery === qf.jql;
          return (
            <button
              key={qf.label}
              onClick={() => handleQuickFilter(qf.jql)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {qf.label}
            </button>
          );
        })}
      </div>

      {!isJql && submittedQuery && (
        <IssueFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          statuses={statuses}
          assignees={assignees}
        />
      )}

      {error && <ErrorState message="Search failed. Check your JQL syntax." />}

      {!submittedQuery && (
        <EmptyState title="Search across all projects">
          Type words to search issue text, or write JQL for precise queries.
        </EmptyState>
      )}

      {isLoading && <LoadingState label="Searching..." />}

      {data && !isLoading && (
        <>
          <IssueList
            issues={allIssues}
            sortField={isJql ? undefined : filters.sortField}
            sortDir={filters.sortDir}
            onSort={isJql ? undefined : handleSort}
          />
          <div className="mt-4">
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={data.total}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}
